import React, { useEffect, useState } from 'react';
import Navigation from '../components/Navigation/Navigation';
import TopBanner from '../components/TopBanner/TopBanner';
import { Outlet } from 'react-router-dom';
import Spinner from '../components/Spinner/Spinner';
import { useSelector, useDispatch } from 'react-redux';
import Footer from '../components/Footer/Footer';
import content from '../data/content.json';
import WhatsAppButton from '../components/common/WhatsAppButton';
import { fetchUserDetails } from '../api/userInfo';
import { loadUserInfo, logoutUser, selectUserInfo } from '../store/features/user';
import { getToken, isTokenValid } from '../utils/jwt-helper';
import { useLocation } from 'react-router-dom';
import { selectCartSidebarOpen } from '../store/features/ui';
import { fetchCart } from '../store/features/cart';
import { fetchFavorites } from '../store/features/favorites';
import Analytics from '../components/Analytics/Analytics';

// ScrollToTop component
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const ShopApplicationWrapper = () => {
  const isLoading = useSelector((state) => state?.commonState?.loading);
  const userInfo = useSelector(selectUserInfo);
  const dispatch = useDispatch();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const cartSidebarOpen = useSelector(selectCartSidebarOpen);

  useEffect(() => {
    // Check if user is logged in but user info is not loaded
    const token = getToken();
    const isLoggedIn = isTokenValid();

    if (isLoggedIn && token && (!userInfo || !userInfo.id)) {
      const ac = new AbortController();
      // Do not block the whole shop with global loading — mobile users can browse while profile syncs.
      fetchUserDetails({ signal: ac.signal })
        .then((res) => {
          dispatch(loadUserInfo(res));
          dispatch(fetchCart());
          dispatch(fetchFavorites());
        })
        .catch((err) => {
          if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
            return;
          }
          console.error('Failed to load user details:', err);
          const status = err?.response?.status;
          // Only 401 means the session/token is invalid. 403 is "forbidden" for this request — do not wipe login.
          if (status === 401) {
            dispatch(logoutUser());
          }
        });
      return () => ac.abort();
    } else if (isLoggedIn && token && userInfo && userInfo.id) {
      dispatch(fetchCart());
      dispatch(fetchFavorites());
    }
  }, [dispatch, userInfo]);

  // Only hide WhatsAppButton on mobile when nav or cart is open
  const hideWhatsAppMobile = (mobileMenuOpen || cartSidebarOpen) && window.innerWidth < 768;
  // Hide footer + WhatsApp on checkout pages to reduce distraction and exit points
  const { pathname } = useLocation();
  const isCheckoutPage = pathname === '/checkout';

  return (
    <div className="min-h-screen flex flex-col">
      <ScrollToTop />
      <Analytics />
      {!isCheckoutPage && <TopBanner />}
      <Navigation onMenuOpenChange={setMobileMenuOpen} />
      <div className="flex-grow">
        <Outlet />
        {isLoading && <Spinner />}
      </div>
      {!isCheckoutPage && <Footer content={content?.footer} />}
      {!hideWhatsAppMobile && !isCheckoutPage && <WhatsAppButton />}
    </div>
  );
};

export default ShopApplicationWrapper;
